import { useState, useEffect } from 'react'
import { useAuth } from '../lib/auth'
import { supabase } from '@/lib/supabase'
import { 
  fetchGames, 
  getDailyGame, 
  canAffordGame, 
  startGame, 
  GAME_MODES 
} from '@/lib/games'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { useNavigate } from 'react-router-dom'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { NotificationToast } from "@/components/ui/notification-toast"
import type { Game, GameMode } from '@/types/games'
import { Gamepad2, Plane } from 'lucide-react'

interface PoolGame {
  id: string;
  maxAmount: number;
  currentPlayers: number;
  requiredPlayers: number;
  entryFee: number;
  status: 'open' | 'in_progress' | 'completed';
  game_type: {
    name: string;
    type: string;
  };
  flight_details?: any;
}

export default function GamesPage() {
  // Existing state
  const [games, setGames] = useState<Game[]>([])
  const [dailyGame, setDailyGame] = useState<Game | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedGame, setSelectedGame] = useState<Game | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [selectedMode, setSelectedMode] = useState<GameMode>('trivia')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedType, setSelectedType] = useState<string>('all')
  
  // New state for pools
  const [creationType, setCreationType] = useState<'regular' | 'pool'>('regular')
  const [poolGames, setPoolGames] = useState<PoolGame[]>([])
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'info';
    message: string;
  } | null>(null)

  const navigate = useNavigate()
  const { user } = useAuth()

  useEffect(() => {
    Promise.all([loadGames(), loadDailyGame(), loadPoolGames()])
      .finally(() => setLoading(false))
  }, [])

  // Your existing games filter logic
  const filteredGames = [...(dailyGame ? [dailyGame] : []), ...games].filter(game => {
    const matchesSearch = game.title?.toLowerCase().includes(searchTerm.toLowerCase()) || false
    const matchesType = selectedType === 'all' || game.type === selectedType
    return matchesSearch && matchesType
  })

  async function loadGames() {
    try {
      const availableGames = await fetchGames()
      setGames(availableGames)
    } catch (error) {
      console.error('Failed to load games:', error)
    }
  }

  async function loadDailyGame() {
    try {
      const game = await getDailyGame()
      setDailyGame(game)
    } catch (error) {
      console.error('Failed to load daily game:', error)
    }
  }

  async function loadPoolGames() {
    try {
      const { data: pools, error } = await supabase
        .from('game_pools')
        .select(`
          *,
          game_type:game_types (
            name,
            type
          )
        `)
        .eq('status', 'open')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPoolGames(pools || []);
    } catch (error) {
      console.error('Failed to load pool games:', error)
    }
  }

  const handleGameSelect = async (game: Game) => {
    if (!user) return

    try {
      const canAfford = await canAffordGame(user.id, game.id)
      if (!canAfford) {
        setNotification({
          type: 'error',
          message: 'Insufficient Voy Coins'
        })
        return
      }
      setSelectedGame(game)
    } catch (error) {
      console.error('Failed to check game affordability:', error)
    }
  }

  const handleConfirmStart = async () => {
    if (!selectedGame || !user) return

    try {
      const started = await startGame(user.id, selectedGame.id)
      if (started) {
        navigate(`/games/${selectedGame.id}`)
      }
    } catch (error) {
      console.error('Failed to start game:', error)
      setNotification({
        type: 'error',
        message: 'Failed to start game'
      })
    }
  }

  const handleCancelStart = () => {
    setSelectedGame(null)
  }

  if (loading) {
    return (
      <div className="container mx-auto py-6">
        <div className="text-center">
          <p className="text-muted-foreground">Loading games...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-6">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-semibold">Games</h1>
          <div className="flex gap-4">
            <Button
              variant="link"
              onClick={() => navigate('/leaderboard')}
              className="text-primary hover:text-primary/90"
            >
              View Leaderboard
            </Button>
            <Button onClick={() => setIsCreating(true)}>Create Game</Button>
          </div>
        </div>

        <Tabs defaultValue="regular" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="regular" className="flex items-center gap-2">
              <Gamepad2 className="w-4 h-4" />
              Regular Games
            </TabsTrigger>
            <TabsTrigger value="pools" className="flex items-center gap-2">
              <Plane className="w-4 h-4" />
              Flight Pools
            </TabsTrigger>
          </TabsList>

          <TabsContent value="regular" className="mt-6">
            <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
              <Input
                placeholder="Search games..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full sm:w-auto sm:max-w-xs"
              />
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant={selectedType === 'all' ? 'default' : 'outline'}
                  onClick={() => setSelectedType('all')}
                >
                  All
                </Button>
                <Button
                  variant={selectedType === 'best_of_three' ? 'default' : 'outline'}
                  onClick={() => setSelectedType('best_of_three')}
                >
                  Best of Three
                </Button>
                <Button
                  variant={selectedType === 'total_score' ? 'default' : 'outline'}
                  onClick={() => setSelectedType('total_score')}
                >
                  Total Score
                </Button>
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 mt-8">
              {filteredGames.map((game) => (
                <Card key={game.id}>
                  <CardHeader>
                    <CardTitle className="flex justify-between items-center">
                      <span>#{game.title || 'Untitled Game'}</span>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs"
                        >
                          {game.is_daily ? 'Free' : `${game.cost_per_game} Coins`}
                        </Button>
                      </div>
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {game.is_daily && 'Win 10 Coins'}
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Game Mode:</span>
                        <span className="capitalize">{game.mode || 'trivia'}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Type:</span>
                        <span>{game.type === 'best_of_three' ? 'Best of 3' : 'Total Score'}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Players</span>
                        <span>{game.current_players}/{game.max_players}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Expires:</span>
                        <span>{new Date(game.expires_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </CardContent>
                  <Button 
                    className="w-full" 
                    onClick={() => handleGameSelect(game)}
                    disabled={!user || (game.is_daily && game.status === 'expired')}
                  >
                    {user ? 'Start Game' : 'Sign in to Play'}
                  </Button>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="pools" className="mt-6">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {poolGames.map((pool) => (
                <Card key={pool.id}>
                  <CardHeader>
                    <CardTitle>
                      {pool.id || 'Untitled Pool'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Game Type:</span>
                        <span>{pool.game_type.name}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Prize Pool:</span>
                        <span className="font-medium">${pool.maxAmount}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Players:</span>
                        <span>{pool.currentPlayers}/{pool.requiredPlayers}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Entry Fee:</span>
                        <span>{pool.entryFee} Coins</span>
                      </div>
                      {pool.flight_details && (
                        <div className="mt-2 p-3 bg-muted rounded-lg">
                          <p className="text-sm font-medium">Flight Details:</p>
                          <p className="text-sm mt-1">
                            {pool.flight_details.origin} → {pool.flight_details.destination}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            ${pool.flight_details.price}
                          </p>
                        </div>
                      )}
                      <Button 
                        className="w-full mt-4"
                        disabled={!user}
                        onClick={async () => {
                          if (!user) return;
                          try {
                            const { data: profile } = await supabase
                              .from('profiles')
                              .select('voy_coins')
                              .eq('id', user.id)
                              .single();

                            if (!profile || profile.voy_coins < pool.entryFee) {
                              setNotification({
                                type: 'error',
                                message: 'Insufficient Voy Coins'
                              });
                              return;
                            }

                            const { error } = await supabase
                              .from('pool_entries')
                              .insert({
                                pool_id: pool.id,
                                user_id: user.id,
                                entry_fee: pool.entryFee,
                                flight_details: pool.flight_details || {}
                              });

                            if (error) throw error;

                            await loadPoolGames();
                            setNotification({
                              type: 'success',
                              message: 'Successfully joined pool!'
                            });
                          } catch (error) {
                            console.error('Failed to join pool:', error);
                            setNotification({
                              type: 'error',
                              message: 'Failed to join pool'
                            });
                          }
                        }}
                      >
                        {user ? 'Join Pool' : 'Sign in to Join'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={isCreating} onOpenChange={setIsCreating}>
        <DialogContent className="max-w-3xl w-full overflow-y-auto max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Create New Game</DialogTitle>
            <DialogDescription>Choose the type of game you want to create</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <Button
              type="button"
              variant={creationType === 'regular' ? "default" : "outline"}
              className="h-auto p-4 flex flex-col items-center gap-2"
              onClick={() => setCreationType('regular')}
            >
              <Gamepad2 className="h-8 w-8" />
              <span className="font-semibold">Regular Game</span>
              <span className="text-xs text-muted-foreground text-center">
                Create a standard game with coins rewards
              </span>
            </Button>
            
            <Button
              type="button"
              variant={creationType === 'pool' ? "default" : "outline"}
              className="h-auto p-4 flex flex-col items-center gap-2"
              onClick={() => setCreationType('pool')}
            >
              <Plane className="h-8 w-8" />
              <span className="font-semibold">Flight Pool</span>
              <span className="text-xs text-muted-foreground text-center">
                Create a pool game with flight prizes
              </span>
            </Button>
          </div>

          {creationType === 'regular' ? (
            <form onSubmit={async (e) => {
              e.preventDefault()
              const form = e.target as HTMLFormElement
              const formData = new FormData(form)
              const modeConfig = GAME_MODES[selectedMode]

              try {
                const { error } = await supabase
                  .from('games')
                  .insert({
                    title: formData.get('title'),
                    type: formData.get('type'),
                    mode: selectedMode,
                    question_type: modeConfig.questionType,
                    cost_per_game: parseInt(formData.get('cost') as string),
                    expires_at: formData.get('expires_at'),
                    max_players: modeConfig.maxPlayers,
                    status: 'open'
                  })

                if (error) throw error
                
                await loadGames()
                setIsCreating(false)
                setNotification({
                  type: 'success',
                  message: 'Game created successfully!'
                })
              } catch (error) {
                console.error('Failed to create game:', error)
                setNotification({
                  type: 'error',
                  message: 'Failed to create game'
                })
              }
            }}>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    name="title"
                    placeholder="Enter game title"
                    defaultValue={`${GAME_MODES[selectedMode].name} Challenge`}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mode">Game Mode</Label>
                  <select
                    id="mode"
                    name="mode"
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1"
                    value={selectedMode}
                    onChange={(e) => setSelectedMode(e.target.value as GameMode)}
                    required
                  >
                    <option value="trivia">Trivia</option>
                    <option value="memory">Memory Match</option>
                    <option value="word_puzzle">Word Puzzle</option>
                    <option value="sequence">Sequence</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="type">Game Type</Label>
                  <select
                    id="type"
                    name="type"
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1"
                    required
                  >
                    <option value="best_of_three">Best of Three</option>
                    <option value="total_score">Total Score</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cost">Cost (Coins)</Label>
                  <Input
                    id="cost"
                    name="cost"
                    type="number"
                    min="10"
                    defaultValue="50"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="expires_at">Expires At</Label>
                  <Input
                    id="expires_at"
                    name="expires_at"
                    type="datetime-local"
                    defaultValue={new Date(Date.now() + 86400000).toISOString().slice(0, 16)}
                    required
                  />
                </div>
              </div>

              <DialogFooter className="mt-6">
                <Button type="button" variant="outline" onClick={() => setIsCreating(false)}>
                  Cancel
                </Button>
                <Button type="submit">Create Game</Button>
              </DialogFooter>
            </form>
          ) : (
            <form onSubmit={async (e) => {
              e.preventDefault()
              const form = e.target as HTMLFormElement
              const formData = new FormData(form)

              try {
                const { error } = await supabase
                  .from('game_pools')
                  .insert({
                    maxAmount: parseFloat(formData.get('maxAmount') as string),
                    requiredPlayers: parseInt(formData.get('requiredPlayers') as string),
                    entryFee: parseInt(formData.get('entryFee') as string),
                    title: formData.get('title'),
                    game_type_id: formData.get('gameTypeId'),
                    status: 'open'
                  })

                if (error) throw error
                
                await loadPoolGames()
                setIsCreating(false)
                setNotification({
                  type: 'success',
                  message: 'Pool created successfully!'
                })
              } catch (error) {
                console.error('Failed to create pool:', error)
                setNotification({
                  type: 'error',
                  message: 'Failed to create pool'
                })
              }
            }}>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    name="title"
                    placeholder="Enter pool title"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gameTypeId">Game Type</Label>
                  <select
                    id="gameTypeId"
                    name="gameTypeId"
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1"
                    required
                  >
                    <option value="trivia">Travel Trivia</option>
                    <option value="memory">Memory Match</option>
                    <option value="word_puzzle">Word Puzzle</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="maxAmount">Max Prize Amount ($)</Label>
                  <Input
                    id="maxAmount"
                    name="maxAmount"
                    type="number"
                    min="100"
                    max="5000"
                    defaultValue="1000"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="requiredPlayers">Required Players</Label>
                  <Input
                    id="requiredPlayers"
                    name="requiredPlayers"
                    type="number"
                    min="2"
                    max="100"
                    defaultValue="10"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="entryFee">Entry Fee (Coins)</Label>
                  <Input
                    id="entryFee"
                    name="entryFee"
                    type="number"
                    min="1"
                    max="100"
                    defaultValue="10"
                    required
                  />
                </div>
              </div>

              <DialogFooter className="mt-6">
                <Button type="button" variant="outline" onClick={() => setIsCreating(false)}>
                  Cancel
                </Button>
                <Button type="submit">Create Pool Game</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedGame} onOpenChange={() => setSelectedGame(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start Game</DialogTitle>
            <DialogDescription>
              This game will cost {selectedGame?.cost_per_game} Voy Coins to play.
              You can play up to 3 times to improve your score.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelStart}>
              Cancel
            </Button>
            <Button onClick={handleConfirmStart}>
              Play ({selectedGame?.cost_per_game} Coins)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {notification && (
        <NotificationToast
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  )
}